/**
 * Mock data for Tour Mode
 *
 * This file contains realistic mock data that is displayed when components
 * are rendered inside the tour modal. The data should closely mirror what
 * users would see in the actual app.
 */

import type { IncomeStatementRecord } from '../Dashboard/Charts/hooks/useCompanyIncomeStatement';
import type { AiEarningsSummary } from '../../types/AiEarningsSummary';
import type { InstitutionalHolder, InstitutionalOwnershipMetadata } from '../Dashboard/Charts/hooks/useInstitutionalOwnership';
import type { Quote } from '../Dashboard/Charts/hooks/useCompanyQuote';
import type { CongressionalTradesResponse } from '../SuperInvestors/hooks/useCompanyCongressionalTrades';
import type { HouseLatest } from '../SuperInvestors/hooks/useHouseLatest';
import type { SenateLatest } from '../SuperInvestors/hooks/useSenateLatest';
import type { MostActiveStocksResponse } from '../../hooks/useMostActiveStocks';
import type { BiggestGainersResponse } from '../../hooks/useBiggestGainers';
import type { StockAnalysisReportData } from '../../types/StockAnalysisReport';
import type { SimpleProjectionYearResult } from '../../types/SimpleProjection';
import { NVDA_PRODUCTION_REPORT } from './tourMockAiAnalysisReportData';
import { getSymbolImageUrl } from '../../lib/symbolImage';

// Locally defined to avoid circular dependencies with hooks that use TourModeContext
export interface CompanyProfile {
  symbol: string;
  companyName: string;
  currency: string;
  cik: string;
  isin: string;
  cusip: string;
  exchangeFullName: string;
  exchange: string;
  industry: string;
  website: string;
  description: string;
  ceo: string;
  sector: string;
  country: string;
  fullTimeEmployees: string;
  phone: string;
  address: string;
  city: string;
  state: string;
  zip: string;
  image: string;
  ipoDate: string;
  defaultImage: boolean;
  isEtf: boolean;
  isActivelyTrading: boolean;
  isAdr: boolean;
  isFund: boolean;
  price: number;
  beta: number;
  lastDividend: number;
  range: string;
  change: number;
  changePercentage: number;
  volume: number;
  averageVolume: number;
  marketCap: number;
}

export interface PriceRecord1Min {
  date: string;
  open: number;
  low: number;
  high: number;
  close: number;
  volume: number;
}

export interface PriceDataWithMetadata1Min {
  data: PriceRecord1Min[];
  requestedDate: string;
  actualDate: string;
  fallbackUsed: boolean;
  recordCount: number;
}

// Dashboard watchlist item type (from /dashboard-watchlist endpoint)
export interface DashboardWatchlistItem {
  id: string;
  ticker: string;
  companyName: string;
  logo: string;
  marketCap: number;
  exchange: string;
  price: number;
  change: number;
  changePercentage: number;
  createdAt: string;
}

// Define all available mock data keys
export type TourDataKey =
  | 'incomeStatement'
  | 'aiEarningsSummary'
  | 'aiAnalysisReport'
  | 'institutionalHolders'
  | 'institutionalMetadata'
  | 'dashboardWatchlist'
  | 'simpleProjections'
  | 'congressionalTrades'
  | 'superInvestorHoldings'
  | 'companyQuote'
  | 'mostActiveStocks'
  | 'biggestGainers'
  | 'companyProfiles'
  | 'price1Min';

/**
 * Mock company profiles for watchlist stocks (for logos in CompactStockCard)
 */
export const MOCK_COMPANY_PROFILES: Record<string, CompanyProfile> = {
  NVDA: {
    symbol: 'NVDA',
    companyName: 'NVIDIA Corporation',
    currency: 'USD',
    cik: '0001045810',
    isin: 'US67066G1040',
    cusip: '67066G104',
    exchangeFullName: 'NASDAQ Global Select',
    exchange: 'NASDAQ',
    industry: 'Semiconductors',
    website: 'https://www.nvidia.com',
    description: "NVIDIA Corporation provides graphics, and compute and networking solutions in the United States, Taiwan, China, and internationally. The company's Graphics segment offers GeForce GPUs for gaming and PCs, the GeForce NOW game streaming service and related infrastructure, and solutions for gaming platforms; Quadro/NVIDIA RTX GPUs for enterprise workstation graphics; vGPU software for cloud-based visual and virtual computing; automotive platforms for infotainment systems; and Omniverse software for building 3D designs and virtual worlds. Its Compute & Networking segment provides Data Center platforms and systems for AI, HPC, and accelerated computing; Mellanox networking and interconnect solutions; automotive AI Cockpit, autonomous driving development agreements, and autonomous vehicle solutions; cryptocurrency mining processors; Jetson for robotics and other embedded platforms; and NVIDIA AI Enterprise and other software. The company's products are used in gaming, professional visualization, datacenter, and automotive markets. NVIDIA Corporation sells its products to original equipment manufacturers, original device manufacturers, system builders, add-in board manufacturers, retailers/distributors, independent software vendors, Internet and cloud service providers, automotive manufacturers and tier-1 automotive suppliers, mapping companies, start-ups, and other ecosystem participants. It has a strategic collaboration with Kroger Co. NVIDIA Corporation was incorporated in 1993 and is headquartered in Santa Clara, California.",
    ceo: 'Jen-Hsun Huang',
    sector: 'Technology',
    country: 'US',
    fullTimeEmployees: '36000',
    phone: '408 486 2000',
    address: '2788 San Tomas Expressway',
    city: 'Santa Clara',
    state: 'CA',
    zip: '95051',
    image: getSymbolImageUrl('NVDA'),
    ipoDate: '1999-01-22',
    defaultImage: false,
    isEtf: false,
    isActivelyTrading: true,
    isAdr: false,
    isFund: false,
    price: 188.015,
    beta: 2.284,
    lastDividend: 0.04,
    range: '86.62-212.19',
    change: 4.325,
    changePercentage: 2.35451,
    volume: 96215581,
    averageVolume: 191740220,
    marketCap: 4577601246175,
  },
  NBIS: {
    symbol: 'NBIS',
    companyName: 'Nebius Group N.V.',
    currency: 'USD',
    cik: '0001513845',
    isin: 'NL0009805522',
    cusip: '',
    exchangeFullName: 'NASDAQ Global Select',
    exchange: 'NASDAQ',
    industry: 'Internet Content & Information',
    website: 'https://group.nebius.com',
    description: "Nebius Group N.V., operates as a technology company that engages in building full-stack infrastructure to service the global AI industry. Its businesses include Nebius, an AI-centric cloud platform built for intensive AI workloads. Nebius builds full-stack infrastructure for AI, including large-scale GPU clusters, cloud platforms, and tools and services for developers. The company's businesses also comprise Toloka AI, a data partner for various stages of generative AI development; TripleTen, an edtech player re-skilling people for careers in tech; and Avride, which develops autonomous driving technology for self-driving cars and delivery robots. The company was formerly known as Yandex N.V. and changed its name to Nebius Group N.V. in August 2024. Nebius Group N.V. was founded in 1989 and is headquartered in Amsterdam, the Netherlands with R&D hubs across Europe, North America and Israel.",
    ceo: 'Arkady Volozh',
    sector: 'Communication Services',
    country: 'NL',
    fullTimeEmployees: '1371',
    phone: '31 202 066 970',
    address: 'Gustav Mahlerlaan 300',
    city: 'Amsterdam',
    state: '',
    zip: '1082 ME',
    image: getSymbolImageUrl('NBIS'),
    ipoDate: '2024-10-21',
    defaultImage: false,
    isEtf: false,
    isActivelyTrading: true,
    isAdr: false,
    isFund: false,
    price: 91.01,
    beta: 1.149,
    lastDividend: 0,
    range: '18.31-141.1',
    change: -2.22,
    changePercentage: -2.38121,
    volume: 6392098,
    averageVolume: 18933619,
    marketCap: 21842400000,
  },
  MSFT: {
    symbol: 'MSFT',
    companyName: 'Microsoft Corporation',
    currency: 'USD',
    cik: '0000789019',
    isin: 'US5949181045',
    cusip: '594918104',
    exchangeFullName: 'NASDAQ Global Select',
    exchange: 'NASDAQ',
    industry: 'Software - Infrastructure',
    website: 'https://www.microsoft.com',
    description: 'Microsoft Corporation develops, licenses, and supports software, services, devices, and solutions worldwide.',
    ceo: 'Satya Nadella',
    sector: 'Technology',
    country: 'US',
    fullTimeEmployees: '228000',
    phone: '425 882 8080',
    address: 'One Microsoft Way',
    city: 'Redmond',
    state: 'WA',
    zip: '98052-6399',
    image: getSymbolImageUrl('MSFT'),
    ipoDate: '1986-03-13',
    defaultImage: false,
    isEtf: false,
    isActivelyTrading: true,
    isAdr: false,
    isFund: false,
    price: 486.5483,
    beta: 1.07,
    lastDividend: 3.4,
    range: '344.79-555.45',
    change: 1.6283,
    changePercentage: 0.33579,
    volume: 5716569,
    averageVolume: 23180471,
    marketCap: 3616518277208,
  },
  META: {
    symbol: 'META',
    companyName: 'Meta Platforms, Inc.',
    currency: 'USD',
    cik: '0001326801',
    isin: 'US30303M1027',
    cusip: '30303M102',
    exchangeFullName: 'NASDAQ Global Select',
    exchange: 'NASDAQ',
    industry: 'Internet Content & Information',
    website: 'https://investor.fb.com',
    description: 'Meta Platforms, Inc. engages in the development of products that enable people to connect and share.',
    ceo: 'Mark Elliot Zuckerberg',
    sector: 'Technology',
    country: 'US',
    fullTimeEmployees: '76834',
    phone: '650 543 4800',
    address: '1601 Willow Road',
    city: 'Menlo Park',
    state: 'CA',
    zip: '94025',
    image: getSymbolImageUrl('META'),
    ipoDate: '2012-05-18',
    defaultImage: false,
    isEtf: false,
    isActivelyTrading: true,
    isAdr: false,
    isFund: false,
    price: 663.86,
    beta: 1.273,
    lastDividend: 2.1,
    range: '479.8-796.25',
    change: 2.36,
    changePercentage: 0.35676,
    volume: 3696342,
    averageVolume: 18511960,
    marketCap: 1673315635108,
  },
  RKLB: {
    symbol: 'RKLB',
    companyName: 'Rocket Lab USA, Inc.',
    currency: 'USD',
    cik: '0001819994',
    isin: 'US7731221062',
    cusip: '773122106',
    exchangeFullName: 'NASDAQ Global Select',
    exchange: 'NASDAQ',
    industry: 'Aerospace & Defense',
    website: 'https://www.rocketlabusa.com',
    description: 'Rocket Lab USA, Inc. provides launch services and space systems.',
    ceo: 'Peter Beck',
    sector: 'Industrials',
    country: 'US',
    fullTimeEmployees: '2000',
    phone: '714 465 5737',
    address: '3881 McGowen Street',
    city: 'Long Beach',
    state: 'CA',
    zip: '90808',
    image: getSymbolImageUrl('RKLB'),
    ipoDate: '2021-08-25',
    defaultImage: false,
    isEtf: false,
    isActivelyTrading: true,
    isAdr: false,
    isFund: false,
    price: 25.87,
    beta: 2.1,
    lastDividend: 0,
    range: '4.00-28.50',
    change: 0.52,
    changePercentage: 2.05,
    volume: 15000000,
    averageVolume: 18000000,
    marketCap: 12000000000,
  },
};

/**
 * Mock 1-minute price data for NVDA (for MiniChart)
 * Other stocks will gracefully not render charts
 */
export const MOCK_PRICE_1MIN: Record<string, PriceDataWithMetadata1Min> = {
  NVDA: {
    data: [
      { date: '2025-12-23 12:50:00', open: 188.1129, low: 188.015, high: 188.125, close: 188.0188, volume: 185988 },
      { date: '2025-12-23 12:45:00', open: 187.935, low: 187.935, high: 187.975, close: 187.96, volume: 234989 },
      { date: '2025-12-23 12:40:00', open: 187.895, low: 187.845, high: 187.9, close: 187.85, volume: 253771 },
      { date: '2025-12-23 12:35:00', open: 187.89, low: 187.785, high: 187.91, close: 187.8493, volume: 341463 },
      { date: '2025-12-23 12:30:00', open: 188.2143, low: 188.21, high: 188.26, close: 188.215, volume: 247336 },
      { date: '2025-12-23 12:25:00', open: 188.31, low: 188.1007, high: 188.3901, close: 188.1007, volume: 463109 },
      { date: '2025-12-23 12:20:00', open: 188.43, low: 188.4, high: 188.45, close: 188.4, volume: 226931 },
      { date: '2025-12-23 12:15:00', open: 188.281, low: 188.2244, high: 188.3815, close: 188.2244, volume: 373550 },
      { date: '2025-12-23 12:10:00', open: 188.435, low: 188.365, high: 188.4531, close: 188.445, volume: 418059 },
      { date: '2025-12-23 12:05:00', open: 188.1915, low: 188.1, high: 188.195, close: 188.125, volume: 288286 },
      { date: '2025-12-23 12:00:00', open: 188.25, low: 188.1478, high: 188.425, close: 188.255, volume: 742353 },
      { date: '2025-12-23 11:55:00', open: 187.88, low: 187.86, high: 187.9786, close: 187.9786, volume: 351008 },
      { date: '2025-12-23 11:50:00', open: 187.665, low: 187.665, high: 187.89, close: 187.89, volume: 566805 },
      { date: '2025-12-23 11:45:00', open: 187.595, low: 187.5886, high: 187.71, close: 187.6765, volume: 244171 },
      { date: '2025-12-23 11:40:00', open: 187.14, low: 187.135, high: 187.4751, close: 187.47, volume: 554924 },
      { date: '2025-12-23 11:35:00', open: 187.445, low: 187.445, high: 187.59, close: 187.48, volume: 431657 },
      { date: '2025-12-23 11:30:00', open: 187.935, low: 187.7186, high: 187.935, close: 187.7186, volume: 691719 },
      { date: '2025-12-23 11:25:00', open: 187.565, low: 187.4286, high: 187.63, close: 187.63, volume: 589735 },
      { date: '2025-12-23 11:20:00', open: 187.115, low: 187.115, high: 187.26, close: 187.205, volume: 740451 },
      { date: '2025-12-23 11:15:00', open: 186.955, low: 186.92, high: 187, close: 186.92, volume: 444791 },
      { date: '2025-12-23 11:10:00', open: 186.78, low: 186.67, high: 186.87, close: 186.6886, volume: 446608 },
      { date: '2025-12-23 11:05:00', open: 186.32, low: 186.32, high: 186.505, close: 186.475, volume: 305346 },
      { date: '2025-12-23 11:00:00', open: 186.4975, low: 186.3601, high: 186.5399, close: 186.38, volume: 446440 },
      { date: '2025-12-23 10:55:00', open: 185.93, low: 185.93, high: 186.0801, close: 186.03, volume: 423451 },
      { date: '2025-12-23 10:50:00', open: 185.9299, low: 185.8, high: 185.9299, close: 185.88, volume: 373283 },
      { date: '2025-12-23 10:45:00', open: 185.77, low: 185.71, high: 185.78, close: 185.78, volume: 342143 },
      { date: '2025-12-23 10:40:00', open: 185.63, low: 185.625, high: 185.815, close: 185.7944, volume: 724211 },
      { date: '2025-12-23 10:35:00', open: 185.145, low: 185.135, high: 185.2086, close: 185.2086, volume: 224205 },
      { date: '2025-12-23 10:30:00', open: 185.3001, low: 185.2318, high: 185.68, close: 185.535, volume: 953866 },
      { date: '2025-12-23 10:25:00', open: 185.47, low: 185.0301, high: 185.47, close: 185.0301, volume: 861361 },
      { date: '2025-12-23 10:20:00', open: 184.43, low: 184.43, high: 184.6, close: 184.565, volume: 370697 },
      { date: '2025-12-23 10:15:00', open: 184.38, low: 184.32, high: 184.38, close: 184.3533, volume: 230013 },
      { date: '2025-12-23 10:10:00', open: 184.36, low: 184.36, high: 184.4686, close: 184.4118, volume: 647225 },
      { date: '2025-12-23 10:05:00', open: 183.7301, low: 183.52, high: 183.78819, close: 183.78819, volume: 418183 },
      { date: '2025-12-23 10:00:00', open: 184.21001, low: 184.03, high: 184.328, close: 184.03, volume: 421272 },
      { date: '2025-12-23 09:55:00', open: 183.89, low: 183.8632, high: 184.0672, close: 183.92, volume: 420415 },
      { date: '2025-12-23 09:50:00', open: 184.325, low: 184.095, high: 184.3386, close: 184.18, volume: 439376 },
      { date: '2025-12-23 09:45:00', open: 184.8, low: 184.605, high: 184.835, close: 184.7, volume: 638660 },
      { date: '2025-12-23 09:40:00', open: 184.22, low: 184.19, high: 184.438, close: 184.25999, volume: 629774 },
      { date: '2025-12-23 09:35:00', open: 184.03, low: 184.0135, high: 184.3154, close: 184.17, volume: 605136 },
      { date: '2025-12-23 09:30:00', open: 182.96, low: 182.92999, high: 184.28999, close: 184.28999, volume: 2764790 },
    ],
    requestedDate: '2025-12-23',
    actualDate: '2025-12-23',
    fallbackUsed: false,
    recordCount: 41,
  },
  MSFT: {
    data: [
      { date: '2025-12-23 12:50:00', open: 486.34, low: 486.24, high: 486.34, close: 486.27, volume: 9399 },
      { date: '2025-12-23 12:45:00', open: 486.57, low: 486.57, high: 486.675, close: 486.6743, volume: 12254 },
      { date: '2025-12-23 12:40:00', open: 486.54, low: 486.49, high: 486.57, close: 486.52, volume: 10446 },
      { date: '2025-12-23 12:35:00', open: 486.47, low: 486.455, high: 486.5733, close: 486.455, volume: 12308 },
      { date: '2025-12-23 12:30:00', open: 486.65, low: 486.63, high: 486.8079, close: 486.74, volume: 8082 },
      { date: '2025-12-23 12:25:00', open: 486.98, low: 486.93, high: 486.9924, close: 486.9743, volume: 15656 },
      { date: '2025-12-23 12:20:00', open: 486.87, low: 486.7988, high: 486.985, close: 486.95, volume: 15632 },
      { date: '2025-12-23 12:15:00', open: 486.64, low: 486.055, high: 486.7957, close: 486.6187, volume: 12870 },
      { date: '2025-12-23 12:10:00', open: 486.855, low: 486.78, high: 486.9168, close: 486.9, volume: 15621 },
      { date: '2025-12-23 12:05:00', open: 486.9616, low: 486.88, high: 486.965, close: 486.8939, volume: 9780 },
      { date: '2025-12-23 12:00:00', open: 486.475, low: 486.45, high: 486.74, close: 486.695, volume: 20147 },
      { date: '2025-12-23 11:55:00', open: 486.53, low: 486.435, high: 486.55, close: 486.535, volume: 10797 },
      { date: '2025-12-23 11:50:00', open: 486.4458, low: 486.44, high: 486.6801, close: 486.6801, volume: 18847 },
      { date: '2025-12-23 11:45:00', open: 486.8058, low: 486.55, high: 486.88, close: 486.55, volume: 19121 },
      { date: '2025-12-23 11:40:00', open: 487.09, low: 487.05, high: 487.15, close: 487.15, volume: 28681 },
      { date: '2025-12-23 11:35:00', open: 486.93, low: 486.93, high: 487.2815, close: 487.21, volume: 114639 },
      { date: '2025-12-23 11:30:00', open: 486.83, low: 486.8, high: 487, close: 486.84, volume: 28306 },
      { date: '2025-12-23 11:25:00', open: 486.74, low: 486.4833, high: 486.9058, close: 486.9058, volume: 20209 },
      { date: '2025-12-23 11:20:00', open: 486.33, low: 486.2572, high: 486.3943, close: 486.3715, volume: 28603 },
      { date: '2025-12-23 11:15:00', open: 486.0543, low: 486.0543, high: 486.19, close: 486.1829, volume: 11825 },
      { date: '2025-12-23 11:10:00', open: 486.15, low: 486.08, high: 486.3415, close: 486.08, volume: 22469 },
      { date: '2025-12-23 11:05:00', open: 485.92, low: 485.92, high: 486.01, close: 485.9889, volume: 19090 },
      { date: '2025-12-23 11:00:00', open: 485.88, low: 485.86, high: 485.96, close: 485.92, volume: 31258 },
      { date: '2025-12-23 10:55:00', open: 485.99, low: 485.85, high: 485.99, close: 485.9429, volume: 29947 },
      { date: '2025-12-23 10:50:00', open: 485.97, low: 485.9222, high: 486.19, close: 486.18, volume: 14835 },
      { date: '2025-12-23 10:45:00', open: 486.3515, low: 486.13, high: 486.3515, close: 486.13, volume: 16317 },
      { date: '2025-12-23 10:40:00', open: 486.065, low: 486.065, high: 486.1401, close: 486.1199, volume: 16415 },
      { date: '2025-12-23 10:35:00', open: 486.09, low: 486.08, high: 486.13, close: 486.0929, volume: 54144 },
      { date: '2025-12-23 10:30:00', open: 485.9, low: 485.7529, high: 485.97, close: 485.955, volume: 47111 },
      { date: '2025-12-23 10:25:00', open: 486, low: 485.78, high: 486, close: 485.8715, volume: 40915 },
      { date: '2025-12-23 10:20:00', open: 485.92, low: 485.92, high: 486.165, close: 486.165, volume: 30223 },
      { date: '2025-12-23 10:15:00', open: 486.15, low: 486.06, high: 486.17, close: 486.13, volume: 19083 },
      { date: '2025-12-23 10:10:00', open: 486.21, low: 485.95, high: 486.2987, close: 485.99, volume: 27726 },
      { date: '2025-12-23 10:05:00', open: 486.2344, low: 486.205, high: 486.3444, close: 486.3, volume: 45372 },
      { date: '2025-12-23 10:00:00', open: 485.76, low: 485.76, high: 486.0388, close: 485.95, volume: 30109 },
      { date: '2025-12-23 09:55:00', open: 485.42, low: 485.42, high: 485.825, close: 485.82, volume: 35347 },
      { date: '2025-12-23 09:50:00', open: 486.35, low: 485.9999, high: 486.35, close: 486, volume: 25109 },
      { date: '2025-12-23 09:45:00', open: 486.19, low: 486.19, high: 486.53, close: 486.328, volume: 68511 },
      { date: '2025-12-23 09:40:00', open: 485.86, low: 485.86, high: 486.21, close: 485.94, volume: 95731 },
      { date: '2025-12-23 09:35:00', open: 485.67, low: 485.225, high: 485.67, close: 485.32, volume: 85662 },
      { date: '2025-12-23 09:30:00', open: 484.98, low: 484.98, high: 486.33, close: 486.33, volume: 359771 },
    ],
    requestedDate: '2025-12-23',
    actualDate: '2025-12-23',
    fallbackUsed: false,
    recordCount: 41,
  },
  NBIS: {
    data: [
      { date: '2025-12-23 12:50:00', open: 90.93, low: 90.77, high: 90.93, close: 90.8538, volume: 4601 },
      { date: '2025-12-23 12:45:00', open: 91.06, low: 90.9705, high: 91.06, close: 91.0327, volume: 7555 },
      { date: '2025-12-23 12:40:00', open: 91.0458, low: 90.89, high: 91.0826, close: 90.89, volume: 8714 },
      { date: '2025-12-23 12:35:00', open: 91.185, low: 91.18, high: 91.27, close: 91.18, volume: 5077 },
      { date: '2025-12-23 12:30:00', open: 91.19, low: 91.0296, high: 91.19, close: 91.15, volume: 16390 },
      { date: '2025-12-23 12:25:00', open: 91.0792, low: 90.91, high: 91.0792, close: 90.9301, volume: 6488 },
      { date: '2025-12-23 12:20:00', open: 91.1, low: 90.9, high: 91.1165, close: 90.9, volume: 12317 },
      { date: '2025-12-23 12:15:00', open: 91.125, low: 90.95, high: 91.15, close: 90.95, volume: 5404 },
      { date: '2025-12-23 12:10:00', open: 91.1701, low: 91.07, high: 91.2, close: 91.0901, volume: 10479 },
      { date: '2025-12-23 12:05:00', open: 91.29, low: 91.1601, high: 91.29, close: 91.1601, volume: 18785 },
      { date: '2025-12-23 12:00:00', open: 91.06, low: 90.945, high: 91.0877, close: 90.945, volume: 7431 },
      { date: '2025-12-23 11:55:00', open: 90.66, low: 90.64, high: 90.8304, close: 90.685, volume: 32802 },
      { date: '2025-12-23 11:50:00', open: 90.6427, low: 90.58, high: 90.78, close: 90.7695, volume: 5046 },
      { date: '2025-12-23 11:45:00', open: 90.6846, low: 90.6403, high: 90.89, close: 90.6851, volume: 7336 },
      { date: '2025-12-23 11:40:00', open: 90.47, low: 90.47, high: 90.73, close: 90.6906, volume: 22381 },
      { date: '2025-12-23 11:35:00', open: 90.55, low: 90.45, high: 90.67, close: 90.5458, volume: 16341 },
      { date: '2025-12-23 11:30:00', open: 90.675, low: 90.6, high: 90.675, close: 90.653, volume: 13606 },
      { date: '2025-12-23 11:25:00', open: 90.522, low: 90.49, high: 90.6, close: 90.5217, volume: 8066 },
      { date: '2025-12-23 11:20:00', open: 90.53, low: 90.42, high: 90.68, close: 90.42, volume: 29961 },
      { date: '2025-12-23 11:15:00', open: 90.32, low: 90.32, high: 90.88, close: 90.83, volume: 82258 },
      { date: '2025-12-23 11:10:00', open: 90.06, low: 89.73, high: 90.115, close: 89.73, volume: 29506 },
      { date: '2025-12-23 11:05:00', open: 89.34, low: 89.25, high: 89.3999, close: 89.25, volume: 15970 },
      { date: '2025-12-23 11:00:00', open: 89.37, low: 89.1, high: 89.385, close: 89.1, volume: 67269 },
      { date: '2025-12-23 10:55:00', open: 89.11, low: 89.1074, high: 89.31, close: 89.2234, volume: 38500 },
      { date: '2025-12-23 10:50:00', open: 89.45, low: 89.36, high: 89.5728, close: 89.5728, volume: 34562 },
      { date: '2025-12-23 10:45:00', open: 89.65, low: 89.39, high: 89.67, close: 89.67, volume: 74284 },
      { date: '2025-12-23 10:40:00', open: 90.05, low: 90.05, high: 90.39, close: 90.3153, volume: 38730 },
      { date: '2025-12-23 10:35:00', open: 89.9155, low: 89.816, high: 89.95, close: 89.83, volume: 10569 },
      { date: '2025-12-23 10:30:00', open: 89.855, low: 89.8, high: 90.045, close: 89.8, volume: 54986 },
      { date: '2025-12-23 10:25:00', open: 90.38, low: 90.23, high: 90.5, close: 90.27, volume: 26654 },
      { date: '2025-12-23 10:20:00', open: 89.98, low: 89.93, high: 90.16, close: 89.93, volume: 56344 },
      { date: '2025-12-23 10:15:00', open: 89.1, low: 89.08, high: 89.19, close: 89.1, volume: 26599 },
      { date: '2025-12-23 10:10:00', open: 89.33, low: 89.33, high: 89.65, close: 89.57, volume: 52700 },
      { date: '2025-12-23 10:05:00', open: 88.86, low: 88.35, high: 88.87, close: 88.87, volume: 63746 },
      { date: '2025-12-23 10:00:00', open: 90.27, low: 90.23, high: 90.6, close: 90.6, volume: 28527 },
      { date: '2025-12-23 09:55:00', open: 90.91, low: 90.7, high: 90.99, close: 90.7, volume: 42037 },
      { date: '2025-12-23 09:50:00', open: 91.91, low: 91.8, high: 92.03, close: 91.91, volume: 18945 },
      { date: '2025-12-23 09:45:00', open: 92.4, low: 92.23, high: 92.56, close: 92.41, volume: 14792 },
      { date: '2025-12-23 09:40:00', open: 92.87, low: 92.26, high: 92.95, close: 92.67, volume: 35897 },
      { date: '2025-12-23 09:35:00', open: 92.15, low: 91.8, high: 92.41, close: 91.81, volume: 33410 },
      { date: '2025-12-23 09:30:00', open: 90.42, low: 90.2, high: 92.4, close: 92.4, volume: 492229 },
    ],
    requestedDate: '2025-12-23',
    actualDate: '2025-12-23',
    fallbackUsed: false,
    recordCount: 41,
  },
  META: {
    data: [
      { date: '2025-12-23 12:50:00', open: 663.94, low: 663.61, high: 664.0203, close: 663.7698, volume: 17415 },
      { date: '2025-12-23 12:45:00', open: 664.245, low: 664.1165, high: 664.3988, close: 664.34, volume: 9255 },
      { date: '2025-12-23 12:40:00', open: 664.78522, low: 664.47, high: 664.78522, close: 664.47, volume: 6561 },
      { date: '2025-12-23 12:35:00', open: 664.19, low: 664.1489, high: 664.4046, close: 664.3422, volume: 7100 },
      { date: '2025-12-23 12:30:00', open: 664.23, low: 663.62, high: 664.69, close: 664.69, volume: 5878 },
      { date: '2025-12-23 12:25:00', open: 664.192, low: 664.16, high: 664.3458, close: 664.3458, volume: 6576 },
      { date: '2025-12-23 12:20:00', open: 663.96, low: 663.7404, high: 664.024, close: 663.8682, volume: 15139 },
      { date: '2025-12-23 12:15:00', open: 664.76, low: 664.6146, high: 664.97, close: 664.6146, volume: 10349 },
      { date: '2025-12-23 12:10:00', open: 665.37, low: 665, high: 665.37, close: 665, volume: 27714 },
      { date: '2025-12-23 12:05:00', open: 665.21, low: 664.66, high: 665.27, close: 664.68, volume: 15207 },
      { date: '2025-12-23 12:00:00', open: 664.31, low: 664.31, high: 664.42, close: 664.3565, volume: 10535 },
      { date: '2025-12-23 11:55:00', open: 664.58, low: 664.5, high: 664.704, close: 664.61, volume: 17049 },
      { date: '2025-12-23 11:50:00', open: 663.6657, low: 663.6657, high: 664.1561, close: 664.045, volume: 10632 },
      { date: '2025-12-23 11:45:00', open: 664.13, low: 664.12, high: 664.3488, close: 664.2, volume: 16191 },
      { date: '2025-12-23 11:40:00', open: 664.78, low: 664.335, high: 664.78, close: 664.66, volume: 19072 },
      { date: '2025-12-23 11:35:00', open: 664.365, low: 664.28, high: 664.7218, close: 664.5288, volume: 22728 },
      { date: '2025-12-23 11:30:00', open: 664.1391, low: 664.1391, high: 664.515, close: 664.5015, volume: 20506 },
      { date: '2025-12-23 11:25:00', open: 663.96, low: 663.6834, high: 663.96, close: 663.9546, volume: 9486 },
      { date: '2025-12-23 11:20:00', open: 663.5532, low: 663.3976, high: 663.5532, close: 663.43, volume: 4355 },
      { date: '2025-12-23 11:15:00', open: 663.14, low: 663.14, high: 663.4733, close: 663.43, volume: 12297 },
      { date: '2025-12-23 11:10:00', open: 663.22, low: 663.0517, high: 663.22, close: 663.0774, volume: 13822 },
      { date: '2025-12-23 11:05:00', open: 663.71, low: 663.48, high: 663.8, close: 663.6275, volume: 5591 },
      { date: '2025-12-23 11:00:00', open: 663.24, low: 662.9331, high: 663.37, close: 663.37, volume: 25271 },
      { date: '2025-12-23 10:55:00', open: 663.78, low: 663.3512, high: 663.8774, close: 663.3512, volume: 14678 },
      { date: '2025-12-23 10:50:00', open: 663.1046, low: 663.04, high: 663.46, close: 663.42, volume: 13223 },
      { date: '2025-12-23 10:45:00', open: 662.68, low: 662.624, high: 662.9003, close: 662.855, volume: 13261 },
      { date: '2025-12-23 10:40:00', open: 662.91, low: 662.7301, high: 663.03, close: 662.7758, volume: 11289 },
      { date: '2025-12-23 10:35:00', open: 663.0446, low: 662.69, high: 663.205, close: 662.7989, volume: 21756 },
      { date: '2025-12-23 10:30:00', open: 662.5545, low: 662.455, high: 662.92, close: 662.92, volume: 14866 },
      { date: '2025-12-23 10:25:00', open: 662.5832, low: 662.3012, high: 662.7998, close: 662.6274, volume: 11415 },
      { date: '2025-12-23 10:20:00', open: 662.83, low: 662.74, high: 663.16, close: 663, volume: 29236 },
      { date: '2025-12-23 10:15:00', open: 660.959, low: 660.88, high: 661.6, close: 661.59, volume: 16365 },
      { date: '2025-12-23 10:10:00', open: 661.86, low: 661.82, high: 662.2096, close: 661.9335, volume: 16295 },
      { date: '2025-12-23 10:05:00', open: 661.45, low: 661.45, high: 662.06, close: 662.06, volume: 31489 },
      { date: '2025-12-23 10:00:00', open: 663.24, low: 663.24, high: 663.7988, close: 663.525, volume: 24263 },
      { date: '2025-12-23 09:55:00', open: 663.21, low: 662.853, high: 663.4717, close: 663.39, volume: 49879 },
      { date: '2025-12-23 09:50:00', open: 664.82, low: 663.565, high: 664.82, close: 663.62, volume: 30228 },
      { date: '2025-12-23 09:45:00', open: 663.31, low: 663.31, high: 664.2, close: 664.03, volume: 48416 },
      { date: '2025-12-23 09:40:00', open: 662.63, low: 662.4634, high: 662.86, close: 662.4634, volume: 27048 },
      { date: '2025-12-23 09:35:00', open: 660.71, low: 660.42, high: 661.53, close: 661.49, volume: 25170 },
      { date: '2025-12-23 09:30:00', open: 659.33, low: 659.33, high: 661.47, close: 661.46, volume: 138885 },
    ],
    requestedDate: '2025-12-23',
    actualDate: '2025-12-23',
    fallbackUsed: false,
    recordCount: 41,
  },
};

// =============================================================================
// MOCK DATA - To be filled in with realistic data from the UI
// =============================================================================

/**
 * Mock income statement data for RevenueChart and ExpensesChart
 * 20 quarters of NVDA data matching the production report revenueData
 */
export const MOCK_INCOME_STATEMENT: IncomeStatementRecord[] = [
  // Latest quarter first (matches production report revenueData)
  {
    symbol: 'NVDA',
    date: '2025-10-26',
    epsActual: 0.81,
    epsEstimated: 0.75,
    revenueActual: 57006000000,
    revenueEstimated: 33000000000,
    ebitdaActual: 37652000000,
    netIncomeActual: 31845000000,
    researchAndDevelopmentExpensesActual: 3090000000,
    generalAndAdministrativeExpensesActual: 400000000,
    sellingAndMarketingExpensesActual: 350000000,
    sellingGeneralAndAdministrativeExpensesActual: 750000000,
    otherExpensesActual: 150000000,
    operatingExpensesActual: 3990000000,
    grossProfitActual: 41614000000,
    operatingIncomeActual: 37624000000,
    lastUpdated: '2025-11-20',
  },
  {
    symbol: 'NVDA',
    date: '2025-07-27',
    epsActual: 0.68,
    epsEstimated: 0.64,
    revenueActual: 46743000000,
    revenueEstimated: 28500000000,
    ebitdaActual: 30200000000,
    netIncomeActual: 26105000000,
    researchAndDevelopmentExpensesActual: 2900000000,
    generalAndAdministrativeExpensesActual: 380000000,
    sellingAndMarketingExpensesActual: 320000000,
    sellingGeneralAndAdministrativeExpensesActual: 700000000,
    otherExpensesActual: 140000000,
    operatingExpensesActual: 3740000000,
    grossProfitActual: 34122000000,
    operatingIncomeActual: 30382000000,
    lastUpdated: '2025-08-28',
  },
  {
    symbol: 'NVDA',
    date: '2025-04-27',
    epsActual: 0.61,
    epsEstimated: 0.57,
    revenueActual: 44062000000,
    revenueEstimated: 24500000000,
    ebitdaActual: 28200000000,
    netIncomeActual: 24103000000,
    researchAndDevelopmentExpensesActual: 2750000000,
    generalAndAdministrativeExpensesActual: 360000000,
    sellingAndMarketingExpensesActual: 300000000,
    sellingGeneralAndAdministrativeExpensesActual: 660000000,
    otherExpensesActual: 130000000,
    operatingExpensesActual: 3540000000,
    grossProfitActual: 32165000000,
    operatingIncomeActual: 28625000000,
    lastUpdated: '2025-05-28',
  },
  {
    symbol: 'NVDA',
    date: '2025-01-26',
    epsActual: 0.55,
    epsEstimated: 0.51,
    revenueActual: 39331000000,
    revenueEstimated: 22000000000,
    ebitdaActual: 25200000000,
    netIncomeActual: 21505000000,
    researchAndDevelopmentExpensesActual: 2600000000,
    generalAndAdministrativeExpensesActual: 340000000,
    sellingAndMarketingExpensesActual: 280000000,
    sellingGeneralAndAdministrativeExpensesActual: 620000000,
    otherExpensesActual: 120000000,
    operatingExpensesActual: 3340000000,
    grossProfitActual: 28712000000,
    operatingIncomeActual: 25372000000,
    lastUpdated: '2025-02-26',
  },
  {
    symbol: 'NVDA',
    date: '2024-10-27',
    epsActual: 0.49,
    epsEstimated: 0.45,
    revenueActual: 35082000000,
    revenueEstimated: 19500000000,
    ebitdaActual: 22300000000,
    netIncomeActual: 19045000000,
    researchAndDevelopmentExpensesActual: 2450000000,
    generalAndAdministrativeExpensesActual: 320000000,
    sellingAndMarketingExpensesActual: 260000000,
    sellingGeneralAndAdministrativeExpensesActual: 580000000,
    otherExpensesActual: 110000000,
    operatingExpensesActual: 3140000000,
    grossProfitActual: 25610000000,
    operatingIncomeActual: 22470000000,
    lastUpdated: '2024-11-20',
  },
  {
    symbol: 'NVDA',
    date: '2024-07-28',
    epsActual: 0.42,
    epsEstimated: 0.40,
    revenueActual: 30040000000,
    revenueEstimated: 16800000000,
    ebitdaActual: 18900000000,
    netIncomeActual: 16384000000,
    researchAndDevelopmentExpensesActual: 2300000000,
    generalAndAdministrativeExpensesActual: 300000000,
    sellingAndMarketingExpensesActual: 240000000,
    sellingGeneralAndAdministrativeExpensesActual: 540000000,
    otherExpensesActual: 100000000,
    operatingExpensesActual: 2940000000,
    grossProfitActual: 21929000000,
    operatingIncomeActual: 18989000000,
    lastUpdated: '2024-08-28',
  },
  {
    symbol: 'NVDA',
    date: '2024-04-28',
    epsActual: 0.37,
    epsEstimated: 0.34,
    revenueActual: 26044000000,
    revenueEstimated: 14500000000,
    ebitdaActual: 16200000000,
    netIncomeActual: 14094000000,
    researchAndDevelopmentExpensesActual: 2150000000,
    generalAndAdministrativeExpensesActual: 280000000,
    sellingAndMarketingExpensesActual: 220000000,
    sellingGeneralAndAdministrativeExpensesActual: 500000000,
    otherExpensesActual: 90000000,
    operatingExpensesActual: 2740000000,
    grossProfitActual: 19012000000,
    operatingIncomeActual: 16272000000,
    lastUpdated: '2024-05-22',
  },
  {
    symbol: 'NVDA',
    date: '2024-01-28',
    epsActual: 0.31,
    epsEstimated: 0.29,
    revenueActual: 22103000000,
    revenueEstimated: 12500000000,
    ebitdaActual: 13600000000,
    netIncomeActual: 11803000000,
    researchAndDevelopmentExpensesActual: 2000000000,
    generalAndAdministrativeExpensesActual: 260000000,
    sellingAndMarketingExpensesActual: 200000000,
    sellingGeneralAndAdministrativeExpensesActual: 460000000,
    otherExpensesActual: 80000000,
    operatingExpensesActual: 2540000000,
    grossProfitActual: 16135000000,
    operatingIncomeActual: 13595000000,
    lastUpdated: '2024-02-21',
  },
  {
    symbol: 'NVDA',
    date: '2023-10-29',
    epsActual: 0.26,
    epsEstimated: 0.24,
    revenueActual: 18120000000,
    revenueEstimated: 10000000000,
    ebitdaActual: 10900000000,
    netIncomeActual: 9495000000,
    researchAndDevelopmentExpensesActual: 1850000000,
    generalAndAdministrativeExpensesActual: 240000000,
    sellingAndMarketingExpensesActual: 180000000,
    sellingGeneralAndAdministrativeExpensesActual: 420000000,
    otherExpensesActual: 70000000,
    operatingExpensesActual: 2340000000,
    grossProfitActual: 13228000000,
    operatingIncomeActual: 10888000000,
    lastUpdated: '2023-11-21',
  },
  {
    symbol: 'NVDA',
    date: '2023-07-30',
    epsActual: 0.19,
    epsEstimated: 0.17,
    revenueActual: 13507000000,
    revenueEstimated: 7500000000,
    ebitdaActual: 7900000000,
    netIncomeActual: 6888000000,
    researchAndDevelopmentExpensesActual: 1700000000,
    generalAndAdministrativeExpensesActual: 220000000,
    sellingAndMarketingExpensesActual: 160000000,
    sellingGeneralAndAdministrativeExpensesActual: 380000000,
    otherExpensesActual: 60000000,
    operatingExpensesActual: 2140000000,
    grossProfitActual: 9860000000,
    operatingIncomeActual: 7720000000,
    lastUpdated: '2023-08-23',
  },
  {
    symbol: 'NVDA',
    date: '2023-04-30',
    epsActual: 0.1,
    epsEstimated: 0.09,
    revenueActual: 7192000000,
    revenueEstimated: 6500000000,
    ebitdaActual: 2800000000,
    netIncomeActual: 2403000000,
    researchAndDevelopmentExpensesActual: 1550000000,
    generalAndAdministrativeExpensesActual: 200000000,
    sellingAndMarketingExpensesActual: 140000000,
    sellingGeneralAndAdministrativeExpensesActual: 340000000,
    otherExpensesActual: 50000000,
    operatingExpensesActual: 1940000000,
    grossProfitActual: 5250000000,
    operatingIncomeActual: 3310000000,
    lastUpdated: '2023-05-24',
  },
  {
    symbol: 'NVDA',
    date: '2023-01-29',
    epsActual: 0.08,
    epsEstimated: 0.08,
    revenueActual: 6051000000,
    revenueEstimated: 6000000000,
    ebitdaActual: 2100000000,
    netIncomeActual: 1803000000,
    researchAndDevelopmentExpensesActual: 1500000000,
    generalAndAdministrativeExpensesActual: 190000000,
    sellingAndMarketingExpensesActual: 130000000,
    sellingGeneralAndAdministrativeExpensesActual: 320000000,
    otherExpensesActual: 45000000,
    operatingExpensesActual: 1865000000,
    grossProfitActual: 4417000000,
    operatingIncomeActual: 2552000000,
    lastUpdated: '2023-02-22',
  },
  {
    symbol: 'NVDA',
    date: '2022-10-30',
    epsActual: 0.06,
    epsEstimated: 0.07,
    revenueActual: 5931000000,
    revenueEstimated: 5800000000,
    ebitdaActual: 1800000000,
    netIncomeActual: 1500000000,
    researchAndDevelopmentExpensesActual: 1450000000,
    generalAndAdministrativeExpensesActual: 180000000,
    sellingAndMarketingExpensesActual: 120000000,
    sellingGeneralAndAdministrativeExpensesActual: 300000000,
    otherExpensesActual: 40000000,
    operatingExpensesActual: 1790000000,
    grossProfitActual: 4330000000,
    operatingIncomeActual: 2540000000,
    lastUpdated: '2022-11-16',
  },
  {
    symbol: 'NVDA',
    date: '2022-07-31',
    epsActual: 0.08,
    epsEstimated: 0.1,
    revenueActual: 6704000000,
    revenueEstimated: 8100000000,
    ebitdaActual: 2200000000,
    netIncomeActual: 1850000000,
    researchAndDevelopmentExpensesActual: 1400000000,
    generalAndAdministrativeExpensesActual: 170000000,
    sellingAndMarketingExpensesActual: 110000000,
    sellingGeneralAndAdministrativeExpensesActual: 280000000,
    otherExpensesActual: 35000000,
    operatingExpensesActual: 1715000000,
    grossProfitActual: 4894000000,
    operatingIncomeActual: 3179000000,
    lastUpdated: '2022-08-24',
  },
  {
    symbol: 'NVDA',
    date: '2022-05-01',
    epsActual: 0.14,
    epsEstimated: 0.13,
    revenueActual: 8288000000,
    revenueEstimated: 8100000000,
    ebitdaActual: 3800000000,
    netIncomeActual: 3280000000,
    researchAndDevelopmentExpensesActual: 1350000000,
    generalAndAdministrativeExpensesActual: 160000000,
    sellingAndMarketingExpensesActual: 100000000,
    sellingGeneralAndAdministrativeExpensesActual: 260000000,
    otherExpensesActual: 30000000,
    operatingExpensesActual: 1640000000,
    grossProfitActual: 6050000000,
    operatingIncomeActual: 4410000000,
    lastUpdated: '2022-05-25',
  },
  {
    symbol: 'NVDA',
    date: '2022-01-30',
    epsActual: 0.13,
    epsEstimated: 0.12,
    revenueActual: 7643000000,
    revenueEstimated: 7420000000,
    ebitdaActual: 3500000000,
    netIncomeActual: 3003000000,
    researchAndDevelopmentExpensesActual: 1300000000,
    generalAndAdministrativeExpensesActual: 150000000,
    sellingAndMarketingExpensesActual: 95000000,
    sellingGeneralAndAdministrativeExpensesActual: 245000000,
    otherExpensesActual: 28000000,
    operatingExpensesActual: 1573000000,
    grossProfitActual: 5579000000,
    operatingIncomeActual: 4006000000,
    lastUpdated: '2022-02-16',
  },
  {
    symbol: 'NVDA',
    date: '2021-10-31',
    epsActual: 0.12,
    epsEstimated: 0.11,
    revenueActual: 7103000000,
    revenueEstimated: 6800000000,
    ebitdaActual: 3200000000,
    netIncomeActual: 2750000000,
    researchAndDevelopmentExpensesActual: 1250000000,
    generalAndAdministrativeExpensesActual: 140000000,
    sellingAndMarketingExpensesActual: 90000000,
    sellingGeneralAndAdministrativeExpensesActual: 230000000,
    otherExpensesActual: 25000000,
    operatingExpensesActual: 1505000000,
    grossProfitActual: 5185000000,
    operatingIncomeActual: 3680000000,
    lastUpdated: '2021-11-17',
  },
  {
    symbol: 'NVDA',
    date: '2021-08-01',
    epsActual: 0.1,
    epsEstimated: 0.1,
    revenueActual: 6507000000,
    revenueEstimated: 6330000000,
    ebitdaActual: 2900000000,
    netIncomeActual: 2490000000,
    researchAndDevelopmentExpensesActual: 1200000000,
    generalAndAdministrativeExpensesActual: 130000000,
    sellingAndMarketingExpensesActual: 85000000,
    sellingGeneralAndAdministrativeExpensesActual: 215000000,
    otherExpensesActual: 22000000,
    operatingExpensesActual: 1437000000,
    grossProfitActual: 4750000000,
    operatingIncomeActual: 3313000000,
    lastUpdated: '2021-08-18',
  },
  {
    symbol: 'NVDA',
    date: '2021-05-02',
    epsActual: 0.09,
    epsEstimated: 0.08,
    revenueActual: 5661000000,
    revenueEstimated: 5410000000,
    ebitdaActual: 2500000000,
    netIncomeActual: 2150000000,
    researchAndDevelopmentExpensesActual: 1150000000,
    generalAndAdministrativeExpensesActual: 120000000,
    sellingAndMarketingExpensesActual: 80000000,
    sellingGeneralAndAdministrativeExpensesActual: 200000000,
    otherExpensesActual: 20000000,
    operatingExpensesActual: 1370000000,
    grossProfitActual: 4133000000,
    operatingIncomeActual: 2763000000,
    lastUpdated: '2021-05-26',
  },
  {
    symbol: 'NVDA',
    date: '2021-01-31',
    epsActual: 0.08,
    epsEstimated: 0.07,
    revenueActual: 5003000000,
    revenueEstimated: 4820000000,
    ebitdaActual: 2200000000,
    netIncomeActual: 1890000000,
    researchAndDevelopmentExpensesActual: 1100000000,
    generalAndAdministrativeExpensesActual: 110000000,
    sellingAndMarketingExpensesActual: 75000000,
    sellingGeneralAndAdministrativeExpensesActual: 185000000,
    otherExpensesActual: 18000000,
    operatingExpensesActual: 1303000000,
    grossProfitActual: 3652000000,
    operatingIncomeActual: 2349000000,
    lastUpdated: '2021-02-24',
  },
];

/**
 * Mock AI earnings summary for NVDA Q3 2026
 */
export const MOCK_AI_SUMMARY: AiEarningsSummary = {
  symbol: 'NVDA',
  period: 'Q3',
  year: 2026,
  date: '2025-11-19',
  headline: 'NVIDIA delivered record Q3 2026 revenue of $57B (+62% YoY), driven by Blackwell GPU ramp and AI infrastructure demand; guided Q4 revenue to $65B (+14% QoQ) with gross margins holding at 74.875%, while signaling $500B in Blackwell+Rubin revenue visibility through 2026 and $3-4T AI infrastructure opportunity by 2030.',
  quarterlySummary: `NVIDIA reported a blockbuster Q3 2026, with revenue soaring to $57 billion, up 62% year-over-year and a record sequential growth of $10 billion (22%). Data center revenue hit $51 billion, up 66% YoY, fueled by Blackwell GPU adoption (GB300 now two-thirds of Blackwell revenue) and networking growth (up 162% YoY to $8.2B). Management emphasized that demand continues to outstrip supply, with hyperscalers sold out and GPU utilization at full capacity. The company has visibility into $500 billion in Blackwell and Rubin revenue through end-2026, with new partnerships including Anthropic (1GW compute commitment) and OpenAI (strategic infrastructure collaboration).

Strategically, NVIDIA is expanding beyond hyperscalers into enterprise, sovereign, and physical AI markets. Partnerships with Palantir, Lowe's, Unilever, and Salesforce demonstrate broad enterprise adoption, while physical AI is gaining traction with manufacturers like Toyota, TSMC, and robotics firms like Agility and Figure. The company also announced 5 million GPU commitments across AI factory projects, including xAI's Colossus 2 and Lilly's AI drug discovery center.

Management's tone was confident and visionary, with Jensen Huang framing AI as three simultaneous platform shifts: accelerated computing (CPU to GPU), generative AI (replacing classical ML), and agentic AI (reasoning systems). He emphasized NVIDIA's unique position as the only architecture serving all three transitions across all industries.`,
  financialHighlights: [
    'Revenue hit $57B in Q3 2026, up 62% YoY and 22% QoQ, with data center revenue at $51B (+66% YoY), driven by Blackwell GPU ramp and networking growth (up 162% YoY to $8.2B).',
    'Gross margins expanded to 73.6% non-GAAP (73.4% GAAP), exceeding outlook, due to data center mix and cost structure improvements; Q4 guidance targets 74.875% gross margin.',
    'Q4 revenue guidance is $65B (+14% QoQ), implying continued Blackwell momentum; inventory grew 32% QoQ and supply commitments up 63% QoQ.',
    'Operating expenses rose 11% non-GAAP YoY, driven by R&D and infrastructure investments; FY27 OpEx guided to $5B non-GAAP.',
    'Cash flow and balance sheet strength support strategic investments; NVIDIA is investing in ecosystem partners (Anthropic, OpenAI) while maintaining disciplined cash management.',
  ],
  businessHighlights: [
    'Blackwell platform gained momentum, with GB300 contributing two-thirds of Blackwell revenue; transition to GB300 was seamless, with shipments to major CSPs and hyperscalers.',
    'Rubin platform on track for 2026 ramp, with silicon back and ecosystem ready; Vera Rubin will deliver x-factor performance improvement over Blackwell.',
    'Enterprise adoption accelerating: Palantir integrated CUDA X libraries, Lowe\'s built supply chain agility, Unilever cut content creation costs by 550%.',
    'Physical AI expanding: Omniverse digital twins adopted by PTC, Siemens, Toyota, TSMC; robotics firms like Agility, Figure, and Amazon Robotics building on NVIDIA\'s platform.',
    'Strategic partnerships deepening: Anthropic committed to 1GW of compute on Grace Blackwell/Rubin; OpenAI collaboration includes building 10+ gigawatts of AI data centers.',
  ],
  risksAndConcerns: [
    'Geopolitical risks in China persist, with H200 sales limited to ~$50M and no sizable orders due to restrictions.',
    'Supply chain constraints remain a bottleneck, with power, memory, and foundry capacity cited as potential growth limiters.',
    'Rising input costs threaten gross margins; while NVIDIA aims to hold margins in mid-70s for FY27, inflationary pressures could pressure profitability.',
    'Competition in AI accelerators is intensifying, though NVIDIA claims multi-year leadership.',
    'Over-reliance on hyperscalers for revenue; while enterprise and sovereign markets are growing, hyperscalers still dominate.',
  ],
  futureCatalysts: [
    'Rubin platform ramp in 2026, expected to deliver x-factor performance improvement over Blackwell.',
    'Anthropic partnership scaling: 1GW compute commitment on Grace Blackwell/Rubin, with deep technical collaboration.',
    'OpenAI infrastructure build-out: Collaboration to build 10+ gigawatts of AI data centers, with potential equity investment.',
    'Enterprise AI adoption accelerating: Partnerships with ServiceNow, CrowdStrike, SAP, and Palantir expanding CUDA\'s reach.',
    'Physical AI and robotics growth: Omniverse digital twins and Jetson powering next-gen robots; this $1T+ opportunity could become a major revenue pillar.',
  ],
  conclusion: `NVIDIA's Q3 2026 results confirm its dominance in the AI infrastructure boom, with record revenue, margins, and demand visibility. The company is not just riding the AI wave—it's defining the architecture for the next decade of computing. The transition from Hopper to Blackwell is accelerating, with GB300 now the primary driver, and Rubin poised to deliver another generational leap in 2026.

The $500B in Blackwell+Rubin revenue visibility through 2026 and $3-4T AI infrastructure opportunity by 2030 provide a compelling long-term growth narrative. Key metrics to watch: Q4 revenue ($65B guidance), gross margin (74.875% target), Blackwell adoption rate (GB300 vs GB200), and enterprise revenue growth.

For investors, NVIDIA remains a core holding with multi-year growth visibility, but should be mindful of valuation and execution risks at this scale. The company's ability to innovate across the full stack—hardware, software, networking—while expanding its ecosystem makes it uniquely positioned to capture the AI revolution.`,
  generatedAt: '2025-12-19T21:47:37.208Z',
};

/**
 * Mock institutional holders data - Top 20 NVDA holders
 */
export const MOCK_INSTITUTIONAL_HOLDERS: InstitutionalHolder[] = [
  {
    investorName: 'VANGUARD GROUP INC',
    cik: '0000102909',
    filingDate: '2025-11-07',
    marketValue: 414866936311,
    sharesNumber: 2223533800,
    ownership: 9.1256,
    changeInSharesNumber: -8911158,
    changeInSharesNumberPercentage: -0.3992,
    changeInOwnership: -0.0085,
    changeInOwnershipPercentage: -0.0926,
    holdingPeriod: 83,
    firstAdded: '2005-03-31',
    avgPricePaid: 13.06,
    quarterEndPrice: 186.58,
    performancePercentage: 18.0961,
    isNew: false,
  },
  {
    investorName: 'BLACKROCK, INC.',
    cik: '0002012383',
    filingDate: '2025-11-12',
    marketValue: 359961170955,
    sharesNumber: 1929259144,
    ownership: 7.9178,
    changeInSharesNumber: 19470172,
    changeInSharesNumberPercentage: 1.0195,
    changeInOwnership: 0.104,
    changeInOwnershipPercentage: 1.3304,
    holdingPeriod: 5,
    firstAdded: '2024-09-30',
    avgPricePaid: 122.42,
    quarterEndPrice: 186.58,
    performancePercentage: 18.0961,
    isNew: false,
  },
  {
    investorName: 'FMR LLC',
    cik: '0000315066',
    filingDate: '2025-11-13',
    marketValue: 183353400537,
    sharesNumber: 982706616,
    ownership: 4.0331,
    changeInSharesNumber: -15271223,
    changeInSharesNumberPercentage: -1.5302,
    changeInOwnership: -0.0501,
    changeInOwnershipPercentage: -1.2271,
    holdingPeriod: 69,
    firstAdded: '2008-09-30',
    avgPricePaid: 2.55,
    quarterEndPrice: 186.58,
    performancePercentage: 18.0961,
    isNew: false,
  },
  {
    investorName: 'STATE STREET CORP',
    cik: '0000093751',
    filingDate: '2025-11-14',
    marketValue: 182863497028,
    sharesNumber: 980029103,
    ownership: 4.0221,
    changeInSharesNumber: 1820241,
    changeInSharesNumberPercentage: 0.1861,
    changeInOwnership: 0.0198,
    changeInOwnershipPercentage: 0.4945,
    holdingPeriod: 50,
    firstAdded: '2013-06-30',
    avgPricePaid: 17.64,
    quarterEndPrice: 186.58,
    performancePercentage: 18.095,
    isNew: false,
  },
  {
    investorName: 'GEODE CAPITAL MANAGEMENT, LLC',
    cik: '0001214717',
    filingDate: '2025-11-12',
    marketValue: 108827799153,
    sharesNumber: 585419652,
    ownership: 2.4026,
    changeInSharesNumber: 6206155,
    changeInSharesNumberPercentage: 1.0715,
    changeInOwnership: 0.0328,
    changeInOwnershipPercentage: 1.3826,
    holdingPeriod: 77,
    firstAdded: '2006-09-30',
    avgPricePaid: 25.39,
    quarterEndPrice: 186.58,
    performancePercentage: 18.1675,
    isNew: false,
  },
  {
    investorName: 'JPMORGAN CHASE & CO',
    cik: '0000019617',
    filingDate: '2025-11-26',
    marketValue: 91167099884,
    sharesNumber: 488619282,
    ownership: 2.0053,
    changeInSharesNumber: 26032415,
    changeInSharesNumberPercentage: 5.6276,
    changeInOwnership: 0.1127,
    changeInOwnershipPercentage: 5.9527,
    holdingPeriod: 50,
    firstAdded: '2013-06-30',
    avgPricePaid: 50.4,
    quarterEndPrice: 186.58,
    performancePercentage: 18.096,
    isNew: false,
  },
  {
    investorName: 'PRICE T ROWE ASSOCIATES INC /MD/',
    cik: '0000080255',
    filingDate: '2025-11-14',
    marketValue: 73837674000,
    sharesNumber: 395742700,
    ownership: 1.6242,
    changeInSharesNumber: -10582655,
    changeInSharesNumberPercentage: -2.6045,
    changeInOwnership: -0.0383,
    changeInOwnershipPercentage: -2.3047,
    holdingPeriod: 74,
    firstAdded: '2007-06-30',
    avgPricePaid: 29.05,
    quarterEndPrice: 186.58,
    performancePercentage: 18.0961,
    isNew: false,
  },
  {
    investorName: 'MORGAN STANLEY',
    cik: '0000895421',
    filingDate: '2025-11-14',
    marketValue: 60257195054,
    sharesNumber: 322956343,
    ownership: 1.3254,
    changeInSharesNumber: -2754465,
    changeInSharesNumberPercentage: -0.8457,
    changeInOwnership: 1.3254,
    changeInOwnershipPercentage: 100,
    holdingPeriod: 50,
    firstAdded: '2013-06-30',
    avgPricePaid: 25.52,
    quarterEndPrice: 186.58,
    performancePercentage: 18.0961,
    isNew: false,
  },
  {
    investorName: 'NORTHERN TRUST CORP',
    cik: '0000073124',
    filingDate: '2025-11-14',
    marketValue: 48406279750,
    sharesNumber: 259439810,
    ownership: 1.0648,
    changeInSharesNumber: -1711649,
    changeInSharesNumberPercentage: -0.6554,
    changeInOwnership: -0.0037,
    changeInOwnershipPercentage: -0.3496,
    holdingPeriod: 50,
    firstAdded: '2013-06-30',
    avgPricePaid: 21.26,
    quarterEndPrice: 186.58,
    performancePercentage: 18.0961,
    isNew: false,
  },
  {
    investorName: 'UBS ASSET MANAGEMENT AMERICAS INC',
    cik: '0000861177',
    filingDate: '2025-11-14',
    marketValue: 36638492759,
    sharesNumber: 196368811,
    ownership: 0.8059,
    changeInSharesNumber: -41153475,
    changeInSharesNumberPercentage: -17.3262,
    changeInOwnership: -0.1659,
    changeInOwnershipPercentage: -17.0717,
    holdingPeriod: 89,
    firstAdded: '2003-09-30',
    avgPricePaid: 61.23,
    quarterEndPrice: 186.58,
    performancePercentage: 18.0961,
    isNew: false,
  },
  {
    investorName: 'BANK OF AMERICA CORP /DE/',
    cik: '0000070858',
    filingDate: '2025-11-14',
    marketValue: 35550159386,
    sharesNumber: 190535745,
    ownership: 0.782,
    changeInSharesNumber: -11164870,
    changeInSharesNumberPercentage: -5.5354,
    changeInOwnership: -0.0433,
    changeInOwnershipPercentage: -5.2446,
    holdingPeriod: 51,
    firstAdded: '2013-03-31',
    avgPricePaid: 50.93,
    quarterEndPrice: 186.58,
    performancePercentage: 18.0961,
    isNew: false,
  },
  {
    investorName: 'LEGAL & GENERAL GROUP PLC',
    cik: '0000764068',
    filingDate: '2025-11-13',
    marketValue: 33808862263,
    sharesNumber: 181203035,
    ownership: 0.7437,
    changeInSharesNumber: 2609560,
    changeInSharesNumberPercentage: 1.4612,
    changeInOwnership: 0.013,
    changeInOwnershipPercentage: 1.7735,
    holdingPeriod: 74,
    firstAdded: '2007-06-30',
    avgPricePaid: 23.49,
    quarterEndPrice: 186.58,
    performancePercentage: 18.0961,
    isNew: false,
  },
  {
    investorName: 'GOLDMAN SACHS GROUP INC',
    cik: '0000886982',
    filingDate: '2025-11-14',
    marketValue: 31352300832,
    sharesNumber: 168036771,
    ownership: 0.6896,
    changeInSharesNumber: 8009717,
    changeInSharesNumberPercentage: 5.0052,
    changeInOwnership: 0.6896,
    changeInOwnershipPercentage: 100,
    holdingPeriod: 72,
    firstAdded: '2007-12-31',
    avgPricePaid: 46.18,
    quarterEndPrice: 186.58,
    performancePercentage: 18.0961,
    isNew: false,
  },
  {
    investorName: 'CAPITAL RESEARCH GLOBAL INVESTORS',
    cik: '0001422848',
    filingDate: '2025-11-13',
    marketValue: 30855564331,
    sharesNumber: 165377852,
    ownership: 0.6787,
    changeInSharesNumber: 22896705,
    changeInSharesNumberPercentage: 16.07,
    changeInOwnership: 0.0958,
    changeInOwnershipPercentage: 16.4273,
    holdingPeriod: 15,
    firstAdded: '2022-03-31',
    avgPricePaid: 104.6,
    quarterEndPrice: 186.58,
    performancePercentage: 18.0961,
    isNew: false,
  },
  {
    investorName: 'BANK OF NEW YORK MELLON CORP',
    cik: '0001390777',
    filingDate: '2025-11-03',
    marketValue: 29192323592,
    sharesNumber: 156460090,
    ownership: 0.6421,
    changeInSharesNumber: -473358,
    changeInSharesNumberPercentage: -0.3016,
    changeInOwnership: 0,
    changeInOwnershipPercentage: 0.0052,
    holdingPeriod: 65,
    firstAdded: '2009-09-30',
    avgPricePaid: 5.13,
    quarterEndPrice: 186.58,
    performancePercentage: 18.0961,
    isNew: false,
  },
  {
    investorName: 'CHARLES SCHWAB INVESTMENT MANAGEMENT INC',
    cik: '0000884546',
    filingDate: '2025-11-10',
    marketValue: 29084154867,
    sharesNumber: 155880346,
    ownership: 0.6397,
    changeInSharesNumber: 211571,
    changeInSharesNumberPercentage: 0.1359,
    changeInOwnership: 0.0028,
    changeInOwnershipPercentage: 0.4441,
    holdingPeriod: 99,
    firstAdded: '2001-03-31',
    avgPricePaid: 22.66,
    quarterEndPrice: 186.58,
    performancePercentage: 18.0961,
    isNew: false,
  },
  {
    investorName: 'WELLINGTON MANAGEMENT GROUP LLP',
    cik: '0000902219',
    filingDate: '2025-11-14',
    marketValue: 26750458028,
    sharesNumber: 143372591,
    ownership: 0.5884,
    changeInSharesNumber: -2860702,
    changeInSharesNumberPercentage: -1.9563,
    changeInOwnership: -0.0099,
    changeInOwnershipPercentage: -1.6545,
    holdingPeriod: 59,
    firstAdded: '2011-03-31',
    avgPricePaid: 58.01,
    quarterEndPrice: 186.58,
    performancePercentage: 18.0961,
    isNew: false,
  },
  {
    investorName: 'INVESCO LTD.',
    cik: '0000914208',
    filingDate: '2025-11-13',
    marketValue: 26642378692,
    sharesNumber: 142793326,
    ownership: 0.586,
    changeInSharesNumber: 4565930,
    changeInSharesNumberPercentage: 3.3032,
    changeInOwnership: 0.0205,
    changeInOwnershipPercentage: 3.6212,
    holdingPeriod: 56,
    firstAdded: '2011-12-31',
    avgPricePaid: 34.62,
    quarterEndPrice: 186.58,
    performancePercentage: 18.0961,
    isNew: false,
  },
  {
    investorName: 'NUVEEN, LLC',
    cik: '0001871926',
    filingDate: '2025-11-12',
    marketValue: 26186758801,
    sharesNumber: 140351371,
    ownership: 0.576,
    changeInSharesNumber: -8396421,
    changeInSharesNumberPercentage: -5.6447,
    changeInOwnership: -0.0326,
    changeInOwnershipPercentage: -5.3543,
    holdingPeriod: 3,
    firstAdded: '2025-03-31',
    avgPricePaid: 111.56,
    quarterEndPrice: 186.58,
    performancePercentage: 18.0961,
    isNew: false,
  },
  {
    investorName: 'CAPITAL WORLD INVESTORS',
    cik: '0001422849',
    filingDate: '2025-11-13',
    marketValue: 25175986733,
    sharesNumber: 134942117,
    ownership: 0.5538,
    changeInSharesNumber: -8035423,
    changeInSharesNumberPercentage: -5.6201,
    changeInOwnership: -0.0312,
    changeInOwnershipPercentage: -5.3296,
    holdingPeriod: 17,
    firstAdded: '2021-09-30',
    avgPricePaid: 56.08,
    quarterEndPrice: 186.58,
    performancePercentage: 18.0961,
    isNew: false,
  },
];

export const MOCK_INSTITUTIONAL_METADATA: InstitutionalOwnershipMetadata = {
  symbol: 'NVDA',
  year: 2025,
  quarter: 3,
  quarterLabel: 'Q3 2025',
  fallbackUsed: false,
};

/**
 * Mock dashboard watchlist with price data
 * Uses /dashboard-watchlist endpoint format with cards showing price, change, and market cap
 */
export const MOCK_DASHBOARD_WATCHLIST: DashboardWatchlistItem[] = [
  {
    id: '27f1d855-6dc1-4cb9-b385-ab55d570830f',
    ticker: 'META',
    companyName: 'Meta Platforms, Inc.',
    logo: getSymbolImageUrl('META'),
    marketCap: 1667367054234,
    exchange: 'NASDAQ',
    price: 661.5,
    change: 2.73,
    changePercentage: 0.41441,
    createdAt: '2025-12-23T13:28:58.700Z',
  },
  {
    id: 'a1b2c3d4-5678-90ab-cdef-1234567890ab',
    ticker: 'MSFT',
    companyName: 'Microsoft Corporation',
    logo: getSymbolImageUrl('MSFT'),
    marketCap: 3250000000000,
    exchange: 'NASDAQ',
    price: 438.72,
    change: -1.28,
    changePercentage: -0.29,
    createdAt: '2025-12-23T10:15:00.000Z',
  },
  {
    id: 'b2c3d4e5-6789-01bc-def0-234567890abc',
    ticker: 'NVDA',
    companyName: 'NVIDIA Corporation',
    logo: getSymbolImageUrl('NVDA'),
    marketCap: 4577601246175,
    exchange: 'NASDAQ',
    price: 188.015,
    change: 4.325,
    changePercentage: 2.35451,
    createdAt: '2025-12-23T09:30:00.000Z',
  },
  {
    id: 'c3d4e5f6-7890-12cd-ef01-34567890abcd',
    ticker: 'NBIS',
    companyName: 'Nebius Group N.V.',
    logo: getSymbolImageUrl('NBIS'),
    marketCap: 21842400000,
    exchange: 'NASDAQ',
    price: 91.01,
    change: -2.22,
    changePercentage: -2.38121,
    createdAt: '2025-12-23T11:45:00.000Z',
  },
  {
    id: 'd4e5f6a7-8901-23de-f012-4567890abcde',
    ticker: 'RKLB',
    companyName: 'Rocket Lab USA, Inc.',
    logo: getSymbolImageUrl('RKLB'),
    marketCap: 12000000000,
    exchange: 'NASDAQ',
    price: 25.87,
    change: 0.52,
    changePercentage: 2.05,
    createdAt: '2025-12-23T08:00:00.000Z',
  },
];

/**
 * Mock congressional trades for NVDA
 */
export const MOCK_CONGRESSIONAL_TRADES: CongressionalTradesResponse = {
  houseTrades: [
    {
      symbol: 'NVDA',
      disclosureDate: '2025-12-22',
      transactionDate: '2025-11-21',
      firstName: 'Dwight',
      lastName: 'Evans',
      office: 'Dwight Evans',
      district: 'PA03',
      owner: '',
      assetDescription: 'NVIDIA Corporation',
      assetType: 'Stock',
      type: 'Purchase',
      amount: '$1,001 - $15,000',
      capitalGainsOver200USD: 'False',
      comment: '',
      link: 'https://disclosures-clerk.house.gov/public_disc/ptr-pdfs/2025/20033667.pdf',
    },
    {
      symbol: 'NVDA',
      disclosureDate: '2025-11-13',
      transactionDate: '2025-10-30',
      firstName: 'Cleo',
      lastName: 'Fields',
      office: 'Cleo Fields',
      district: 'LA06',
      owner: '',
      assetDescription: 'NVIDIA Corporation',
      assetType: 'Stock',
      type: 'Purchase',
      amount: '$100,001 - $250,000',
      capitalGainsOver200USD: 'False',
      comment: '',
      link: 'https://disclosures-clerk.house.gov/public_disc/ptr-pdfs/2025/20033424.pdf',
    },
    {
      symbol: 'NVDA',
      disclosureDate: '2025-10-29',
      transactionDate: '2025-06-26',
      firstName: 'Lisa',
      lastName: 'McClain',
      office: 'Lisa McClain',
      district: 'MI09',
      owner: 'Spouse',
      assetDescription: 'NVIDIA Corporation',
      assetType: 'Stock',
      type: 'Sale',
      amount: '$50,001 - $100,000',
      capitalGainsOver200USD: 'False',
      comment: '',
      link: 'https://disclosures-clerk.house.gov/public_disc/ptr-pdfs/2025/8221212.pdf',
    },
    {
      symbol: 'NVDA',
      disclosureDate: '2025-10-03',
      transactionDate: '2025-09-05',
      firstName: 'Ro',
      lastName: 'Khanna',
      office: 'Ro Khanna',
      district: 'CA17',
      owner: '',
      assetDescription: 'NVIDIA Corporation',
      assetType: 'Stock',
      type: 'Sale',
      amount: '$1,001 - $15,000',
      capitalGainsOver200USD: 'False',
      comment: '',
      link: 'https://disclosures-clerk.house.gov/public_disc/ptr-pdfs/2025/9115679.pdf',
    },
  ] as HouseLatest[],
  senateTrades: [
    {
      symbol: 'NVDA',
      disclosureDate: '2025-11-03',
      transactionDate: '2025-10-20',
      firstName: 'Sheldon',
      lastName: 'Whitehouse',
      office: 'Sheldon Whitehouse',
      district: 'RI',
      owner: 'Self',
      assetDescription: 'Nvidia Corp',
      assetType: 'Stock',
      type: 'Sale (Partial)',
      amount: '$15,001 - $50,000',
      comment: '--',
      link: 'https://efdsearch.senate.gov/search/view/ptr/51a44263-fbff-415d-81fb-abf32d197db9/',
    },
    {
      symbol: 'NVDA',
      disclosureDate: '2025-10-03',
      transactionDate: '2025-09-04',
      firstName: 'Sheldon',
      lastName: 'Whitehouse',
      office: 'Sheldon Whitehouse',
      district: 'RI',
      owner: 'Self',
      assetDescription: 'NVIDIA Corporation',
      assetType: 'Stock',
      type: 'Sale',
      amount: '$15,001 - $50,000',
      comment: '',
      link: 'https://efdsearch.senate.gov/search/view/ptr/12bfb6c8-5dbf-4f2a-994b-c936a0a0a91d/',
    },
    {
      symbol: 'NVDA',
      disclosureDate: '2025-09-13',
      transactionDate: '2025-08-22',
      firstName: 'John',
      lastName: 'Boozman',
      office: 'John Boozman',
      district: 'AR',
      owner: 'Joint',
      assetDescription: 'NVIDIA Corporation',
      assetType: 'Stock',
      type: 'Purchase',
      amount: '$1,001 - $15,000',
      comment: '',
      link: 'https://efdsearch.senate.gov/search/view/ptr/c104f1ee-c957-4485-9807-e4798463c3b9/',
    },
  ] as SenateLatest[],
  ticker: 'NVDA',
  totalCount: 7,
};


/**
 * Mock most active stocks for Trending tab
 * Using well-known companies with recognizable logos
 */
export const MOCK_MOST_ACTIVE_STOCKS: MostActiveStocksResponse = {
  data: {
    stocks: [
      {
        symbol: 'QS',
        price: 11.51,
        name: 'QuantumScape Corporation',
        change: 0.08,
        changesPercentage: 0.69991,
        exchange: 'NYSE',
      },
      {
        symbol: 'NVDA',
        price: 137.49,
        name: 'NVIDIA Corporation',
        change: 3.21,
        changesPercentage: 2.39,
        exchange: 'NASDAQ',
      },
    ],
    lastUpdated: '2025-12-23T15:23:02.589Z',
    totalCount: 2,
  },
};

/**
 * Mock AI Analysis Report for NVDA - CORE FEATURE
 * This is production data imported from nvdaReportData.ts
 * Generated from actual API response for realistic tour preview
 */
export const MOCK_AI_ANALYSIS_REPORT: StockAnalysisReportData = NVDA_PRODUCTION_REPORT.report;

/**
 * Mock biggest gainers for Top Gainers tab
 * Using well-known companies with recognizable logos
 */
export const MOCK_BIGGEST_GAINERS: BiggestGainersResponse = {
  data: {
    stocks: [
      {
        symbol: 'AAPL',
        price: 271.54,
        name: 'Apple Inc.',
        change: 0.57,
        changesPercentage: 0.21,
        exchange: 'NASDAQ',
      },
      {
        symbol: 'RDDT',
        price: 230.18,
        name: 'Reddit, Inc.',
        change: 5.42,
        changesPercentage: 2.41,
        exchange: 'NYSE',
      },
    ],
    lastUpdated: '2025-12-23T15:26:45.911Z',
    totalCount: 2,
  },
};

/**
 * Mock simple projections for NVDA
 * Base data with modified params: 15% revenue growth, 60% margin, PE ratio 45
 * Shares outstanding (diluted): ~24.79B
 */
export const MOCK_SIMPLE_PROJECTIONS: SimpleProjectionYearResult[] = [
  // Year 0 - Historical (FY2025)
  {
    year: 0,
    yearLabel: '2025',
    isHistorical: true,
    revenue: 130497000000,
    revenueGrowthPct: 0,
    netIncome: 78298200000, // 60% margin applied
    netIncomeGrowthPct: 0,
    netIncomeMargin: 0.60,
    eps: 3.16,
    peRatioLow: 45,
    peRatioHigh: 67.5,
    sharePriceLow: 142.20,
    sharePriceHigh: 213.30,
  },
  // Year 1 - Projection (FY2026)
  {
    year: 1,
    yearLabel: '2026',
    isHistorical: false,
    revenue: 150071550000, // +15%
    revenueGrowthPct: 0.15,
    netIncome: 90042930000,
    netIncomeGrowthPct: 0.15,
    netIncomeMargin: 0.60,
    eps: 3.63,
    peRatioLow: 45,
    peRatioHigh: 67.5,
    sharePriceLow: 163.35,
    sharePriceHigh: 245.03,
  },
  // Year 2 - Projection (FY2027)
  {
    year: 2,
    yearLabel: '2027',
    isHistorical: false,
    revenue: 172582282500, // +15%
    revenueGrowthPct: 0.15,
    netIncome: 103549369500,
    netIncomeGrowthPct: 0.15,
    netIncomeMargin: 0.60,
    eps: 4.18,
    peRatioLow: 45,
    peRatioHigh: 67.5,
    sharePriceLow: 188.10,
    sharePriceHigh: 282.15,
  },
  // Year 3 - Projection (FY2028)
  {
    year: 3,
    yearLabel: '2028',
    isHistorical: false,
    revenue: 198469624875, // +15%
    revenueGrowthPct: 0.15,
    netIncome: 119081774925,
    netIncomeGrowthPct: 0.15,
    netIncomeMargin: 0.60,
    eps: 4.80,
    peRatioLow: 45,
    peRatioHigh: 67.5,
    sharePriceLow: 216.00,
    sharePriceHigh: 324.00,
  },
  // Year 4 - Projection (FY2029)
  {
    year: 4,
    yearLabel: '2029',
    isHistorical: false,
    revenue: 228240068606, // +15%
    revenueGrowthPct: 0.15,
    netIncome: 136944041164,
    netIncomeGrowthPct: 0.15,
    netIncomeMargin: 0.60,
    eps: 5.52,
    peRatioLow: 45,
    peRatioHigh: 67.5,
    sharePriceLow: 248.40,
    sharePriceHigh: 372.60,
  },
  // Year 5 - Projection (FY2030)
  {
    year: 5,
    yearLabel: '2030',
    isHistorical: false,
    revenue: 262476078897, // +15%
    revenueGrowthPct: 0.15,
    netIncome: 157485647338,
    netIncomeGrowthPct: 0.15,
    netIncomeMargin: 0.60,
    eps: 6.35,
    peRatioLow: 45,
    peRatioHigh: 67.5,
    sharePriceLow: 285.75,
    sharePriceHigh: 428.63,
  },
];

/**
 * Mock company quote for NVDA
 */
export const MOCK_COMPANY_QUOTE: Quote = {
  symbol: 'NVDA',
  name: 'NVIDIA Corporation',
  price: 189.21,
  changePercentage: 1.4918,
  change: 2.78,
  volume: 126100993,
  dayLow: 186.43,
  dayHigh: 190.12,
  yearHigh: 212.19,
  yearLow: 86.62,
  marketCap: 4693164840000,
  priceAvg50: 185.6622,
  priceAvg200: 157.916,
  exchange: 'NASDAQ',
  open: 187.50,
  previousClose: 186.43,
  timestamp: 1735056000,
};

// =============================================================================
// COMBINED MOCK DATA OBJECT
// =============================================================================

export const TOUR_MOCK_DATA: Record<TourDataKey, unknown> = {
  incomeStatement: MOCK_INCOME_STATEMENT,
  aiEarningsSummary: MOCK_AI_SUMMARY,
  aiAnalysisReport: MOCK_AI_ANALYSIS_REPORT,
  institutionalHolders: MOCK_INSTITUTIONAL_HOLDERS,
  institutionalMetadata: MOCK_INSTITUTIONAL_METADATA,
  dashboardWatchlist: MOCK_DASHBOARD_WATCHLIST,
  simpleProjections: MOCK_SIMPLE_PROJECTIONS,
  congressionalTrades: MOCK_CONGRESSIONAL_TRADES,
  superInvestorHoldings: null, // Optional - complex structure
  companyQuote: MOCK_COMPANY_QUOTE,
  mostActiveStocks: MOCK_MOST_ACTIVE_STOCKS,
  biggestGainers: MOCK_BIGGEST_GAINERS,
  companyProfiles: MOCK_COMPANY_PROFILES,
  price1Min: MOCK_PRICE_1MIN,
};
